"use client";

import {
  checkMidnightReset,
  endSessionOnPageHide,
  isUsingDebugTimeout,
  pauseSession,
  recordActivity,
  startSessionTimer,
} from "@/analytics/session";
import { UserEvents, trackEvent } from "@punchup/analytics";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { Database } from "@punchup/data/supabase.types";
import { useCallback, useEffect, useRef } from "react";

import dayjs from "@punchup/utils/dayjs";

export default function SessionTracker() {
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const videoKeepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastVideoActivityTime = useRef<number>(-1);
  const activeVideoRefs = useRef<Set<HTMLVideoElement>>(new Set());

  const isAnyVideoPlaying = () => {
    let isPlaying = false;
    activeVideoRefs.current.forEach((video) => {
      if (!video.paused && !video.ended && video.currentTime > 0) {
        isPlaying = true;
      }
    });
    return isPlaying;
  };

  // Set up video keep-alive interval based on debug mode
  const setupVideoKeepAlive = useCallback(() => {
    if (videoKeepAliveIntervalRef.current) {
      clearInterval(videoKeepAliveIntervalRef.current);
      videoKeepAliveIntervalRef.current = null;
    }

    // In debug mode (15s timeout), keep alive every 5 seconds during video playback
    // In production mode (30m timeout), keep alive every 5 minutes
    const keepAliveInterval = isUsingDebugTimeout() ? 5000 : 300000;

    videoKeepAliveIntervalRef.current = setInterval(() => {
      if (isAnyVideoPlaying()) {
        recordActivity();
      }
    }, keepAliveInterval);

    return videoKeepAliveIntervalRef.current;
  }, []);

  useEffect(() => {
    // Copy ref value to avoid stale closure issues in cleanup
    const activeVideos = activeVideoRefs.current;

    startSessionTimer();

    // Setup initial video keep-alive interval
    setupVideoKeepAlive();

    // Standard user activity tracking with debounce
    const handleActivity = () => {
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }

      activityTimeoutRef.current = setTimeout(() => {
        recordActivity();
      }, 500);
    };

    // Video-specific event handlers
    const handleVideoPlay = (e: Event) => {
      if (e.target) {
        const video = e.target as HTMLVideoElement;
        activeVideoRefs.current.add(video);
        recordActivity();
      }
    };

    const handleVideoPause = (e: Event) => {
      if (e.target) {
        recordActivity();
      }
    };

    const handleVideoTimeUpdate = (e: Event) => {
      if (e.target) {
        const video = e.target as HTMLVideoElement;
        const now = dayjs().valueOf();

        // Don't record activity too frequently
        // In debug mode (15s timeout), check every 2 seconds
        // In production mode (30m timeout), check every 60 seconds
        const minInterval = isUsingDebugTimeout() ? 2000 : 60000;

        if (
          now - lastVideoActivityTime.current > minInterval &&
          !video.paused &&
          !video.ended &&
          video.currentTime > 0
        ) {
          lastVideoActivityTime.current = now;
          recordActivity();
        }
      }
    };

    const handleVideoEnded = (e: Event) => {
      if (e.target) {
        const video = e.target as HTMLVideoElement;
        activeVideoRefs.current.delete(video);
      }
    };

    const handleVideoSeek = (e: Event) => {
      recordActivity();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseSession();
      } else {
        recordActivity();
      }
    };

    const handleBlur = () => {
      pauseSession();
    };

    const handleFocus = () => {
      recordActivity();
    };

    const handlePageHide = () => {
      endSessionOnPageHide();
    };

    const handlePageShow = () => {
      recordActivity();
    };

    // Don't end session on page refresh, only on actual unload
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      recordActivity();
    };

    // Check if we crossed midnight without resetting activity timestamp (mixpanel parity)
    const midnightCheckInterval = setInterval(() => {
      checkMidnightReset();
    }, 60000);

    // User activity trackers
    window.addEventListener("click", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity);
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("touchstart", handleActivity);

    // Video playback trackers
    document.addEventListener("play", handleVideoPlay, true);
    document.addEventListener("playing", handleVideoPlay, true);
    document.addEventListener("pause", handleVideoPause, true);
    document.addEventListener("timeupdate", handleVideoTimeUpdate, true);
    document.addEventListener("seeking", handleVideoSeek, true);
    document.addEventListener("seeked", handleVideoSeek, true);
    document.addEventListener("ended", handleVideoEnded, true);

    // Page visibility trackers
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("touchstart", handleActivity);

      document.removeEventListener("play", handleVideoPlay, true);
      document.removeEventListener("playing", handleVideoPlay, true);
      document.removeEventListener("pause", handleVideoPause, true);
      document.removeEventListener("timeupdate", handleVideoTimeUpdate, true);
      document.removeEventListener("seeking", handleVideoSeek, true);
      document.removeEventListener("seeked", handleVideoSeek, true);
      document.removeEventListener("ended", handleVideoEnded, true);

      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("beforeunload", handleBeforeUnload);

      clearInterval(midnightCheckInterval);

      if (videoKeepAliveIntervalRef.current) {
        clearInterval(videoKeepAliveIntervalRef.current);
        videoKeepAliveIntervalRef.current = null;
      }

      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
        activityTimeoutRef.current = null;
      }

      // Use the copied ref value from the beginning of the effect
      activeVideos.clear();

      // Don't end the session on component unmount (which happens during refresh)
      // The session will be restored from localStorage when the component remounts
      // Only end the session in the beforeunload event when the user actually leaves
    };
  }, [setupVideoKeepAlive]);

  // Update video keep-alive interval when debug mode changes
  useEffect(() => {
    setupVideoKeepAlive();

    return () => {
      if (videoKeepAliveIntervalRef.current) {
        clearInterval(videoKeepAliveIntervalRef.current);
        videoKeepAliveIntervalRef.current = null;
      }
    };
  }, [setupVideoKeepAlive]);

  useEffect(() => {
    const supabase = createClientComponentClient<Database>();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        (event === "INITIAL_SESSION" || event === "SIGNED_IN") &&
        session?.user
      ) {
        trackEvent(UserEvents.AUTHENTICATED_SESSION_STARTED);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  return null;
}
